import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Divider, Drawer, IconButton, Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BugReportIcon from '@mui/icons-material/BugReport';
import ReactJson from 'react-json-view';
import moment from 'moment';
import UsageService from '../../services/usage.service';
import { UsageKindChip, UsageStatusChip, UsageApiChip, UsageDirectApiChip, UsageGeoAttribution, clientLabel, isBrowserUa, isPermissionDenial, fmtDateTime, fmtMs, fmtInt } from './usage-widgets';
import { t } from './usage-i18n';

// ---------------------------------------------------------------------------
// Drawer com TUDO de uma linha do histórico: url completa, user-agent, request_id, details
// (filtros, forwarded, error) no JSON viewer do projeto.
// ---------------------------------------------------------------------------

function Row({ label, children, mono }) {
  return (
    <Box style={{ marginBottom: 10 }}>
      <Typography variant="caption" style={{ color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
      <Typography variant="body2" style={{ wordBreak: 'break-all', fontFamily: mono ? 'monospace' : undefined }}>{children ?? '—'}</Typography>
    </Box>
  );
}

export default function UsageEventDrawer({ intl, open, row, onClose, onCopy, lang, history, volumeAlertRows }) {
  const volumeLimit = Number(volumeAlertRows) || 5000; // item 5: limiar por contratante (vem da página)
  // Exceções do request (sob demanda, botão "ver exceção"); zera ao trocar de linha.
  const [exc, setExc] = useState({ loading: false, loaded: false, items: [], error: null });
  useEffect(() => { setExc({ loading: false, loaded: false, items: [], error: null }); }, [row && row.id]);
  if (!row) return null;
  const isScreen = row.actionKind === 'screen';
  // Exceção só existe para 500 (middleware) — um 4xx é resposta tratada: o motivo está em details.errors.
  const canHaveException = !isScreen && (row.statusCode >= 500 || (row.details && row.details.error));
  const reason = row.details && row.details.errors;
  const is4xx = !isScreen && row.statusCode >= 400 && row.statusCode < 500;
  const loadExceptions = () => {
    setExc({ loading: true, loaded: false, items: [], error: null });
    UsageService.exceptionByRequest(row.requestId, 5)
      .then((items) => setExc({ loading: false, loaded: true, items, error: null }))
      .catch((err) => setExc({ loading: false, loaded: true, items: [], error: err.message }));
  };
  const copy = (v) => { try { navigator.clipboard.writeText(String(v)); if (onCopy) onCopy(); } catch (e) { /* sem clipboard */ } };
  const anon = t(intl, 'usage.anonymous', 'anônimo');
  return (
    // className usage-event-drawer: o CSS global pinta o drawer direito de verde (é o drawer de ações
    // rápidas); mexx-global-override.css neutraliza para esta classe.
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ className: 'usage-event-drawer', style: { width: 520, maxWidth: '100%', backgroundColor: '#fff' } }}>
      <Box style={{ padding: 16 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <UsageKindChip intl={intl} kind={row.actionKind} details={row.details} />
            <UsageApiChip api={row.api} />
            {!isScreen && <UsageStatusChip code={row.statusCode} />}
          </Box>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
        <Typography variant="h6" style={{ marginTop: 8, wordBreak: 'break-all' }}>{isScreen ? row.path : row.route}</Typography>
        <Typography variant="caption" style={{ color: '#888' }}>{fmtDateTime(row.occurredAt, true)}</Typography>
        <Divider style={{ margin: '12px 0' }} />

        <Row label={t(intl, 'usage.drawer.user', 'Usuário')}>{row.userName ? `${row.userName} (#${row.userId})` : row.userId ? `#${row.userId}` : anon} · {row.userKind}</Row>
        {row.userEmail && <Row label={t(intl, 'usage.drawer.email', 'E-mail')} mono>
          {row.userEmail}
          <IconButton size="small" title={t(intl, 'usage.drawer.copyEmail', 'Copiar e-mail')} onClick={() => copy(row.userEmail)} style={{ marginLeft: 4 }}><ContentCopyIcon style={{ fontSize: 14 }} /></IconButton>
        </Row>}
        {!isScreen && <Row label={t(intl, 'usage.drawer.methodRoute', 'Método / rota')} mono>{row.httpMethod} {row.route}</Row>}
        <Row label="URL" mono>
          {row.url}
          {row.url && <IconButton size="small" onClick={() => copy(row.url)} style={{ marginLeft: 4 }}><ContentCopyIcon style={{ fontSize: 14 }} /></IconButton>}
        </Row>
        {/* ORIGEM: toda linha é uma requisição de API. 'Tela' aqui é de onde ela partiu, não o que ela é. */}
        <Row label={t(intl, 'usage.drawer.origin', 'Origem da requisição')}>
          {isScreen ? (
            <span>{t(intl, 'usage.drawer.origin.screenRow', 'Abertura de tela no sistema')} · <span style={{ fontFamily: 'monospace' }}>{row.path}</span></span>
          ) : row.screenVisitId ? (
            <span>{t(intl, 'usage.drawer.origin.fromScreen', 'Feita a partir da tela')} <span style={{ fontFamily: 'monospace' }}>{row.screen || row.screenRoute}</span> · {clientLabel(intl, row.userAgent)}</span>
          ) : row.actionKind === 'login' || row.actionKind === 'logout' ? (
            <span>{t(intl, 'usage.drawer.origin.auth', 'Tela de entrada (não envia visita)')} · {clientLabel(intl, row.userAgent)}</span>
          ) : isBrowserUa(row.userAgent) ? (
            <span><b>{t(intl, 'usage.origin.noScreen', 'Sem tela')} · {clientLabel(intl, row.userAgent)}</b> <span style={{ color: '#666' }}>{t(intl, 'usage.drawer.origin.browser', 'Navegador, mas sem tela do sistema: chamada do próprio front fora de uma tela registrada (menu, notificações, login), ou URL colada no navegador. Não é script.')}</span></span>
          ) : (
            <span><UsageDirectApiChip intl={intl} client={clientLabel(intl, row.userAgent)} /> <span style={{ color: '#c62828' }}>{t(intl, 'usage.drawer.origin.direct', 'Fora do sistema: cliente que não é navegador (script, ferramenta). O User-Agent está abaixo.')}</span></span>
          )}
        </Row>
        {row.screenVisitId && (
          <Row label={t(intl, 'usage.drawer.visit', 'Visita')} mono>
            {row.screenVisitId}
            {history && row.userId ? (
              <Button size="small" style={{ marginLeft: 8 }} onClick={() => { const day = moment(row.occurredAt).format('YYYY-MM-DD'); if (onClose) onClose(); history.push(`/${lang}/usage/user-activity?userId=${row.userId}&userName=${encodeURIComponent(row.userName || '')}&start=${day}&end=${day}&visit=${row.screenVisitId}`); }}>
                {t(intl, 'usage.drawer.viewVisit', 'ver a visita')}
              </Button>
            ) : null}
          </Row>
        )}
        {row.entityType && (
          <Row label={t(intl, 'usage.drawer.entity', 'Entidade')}>
            {row.entityType}{row.entityId ? ` #${row.entityId}` : ''}
            {row.entityType === 'called' && row.entityId && history && (
              <Button size="small" style={{ marginLeft: 8 }} onClick={() => history.push(`/${lang}/called-accompaniment/${row.entityId}`)}>{t(intl, 'usage.drawer.openCalled', 'abrir chamado')}</Button>
            )}
            {row.entityId && history && (
              <Button size="small" style={{ marginLeft: 4 }} onClick={() => history.push(`/${lang}/usage/entity-access?entityType=${row.entityType}&entityId=${row.entityId}`)}>{t(intl, 'usage.drawer.whoAccessed', 'quem acessou')}</Button>
            )}
          </Row>
        )}
        {!isScreen && <Row label={t(intl, 'usage.drawer.duration', 'Duração')}>{fmtMs(row.durationMs)}</Row>}
        {/* Item 5: quantas linhas a resposta/arquivo levou; details.total = total da página quando maior. */}
        {!isScreen && row.resultCount !== null && row.resultCount !== undefined && (
          <Row label={t(intl, 'usage.drawer.rows', 'Linhas devolvidas')}>
            <span style={{ color: Number(row.resultCount) >= volumeLimit ? '#c62828' : undefined, fontWeight: Number(row.resultCount) >= volumeLimit ? 700 : undefined }}>{fmtInt(row.resultCount)}</span>
            {row.details && row.details.total ? ` (${t(intl, 'usage.drawer.rowsTotal', 'de um total de')} ${fmtInt(Number(row.details.total))})` : ''}
          </Row>
        )}
        <Row label="IP">
          {row.ip}{row.details && row.details.forwarded ? <span style={{ color: '#c62828', marginLeft: 8 }}>⚠ {t(intl, 'usage.forgedChain', 'cadeia X-Forwarded-For')}: {row.details.forwarded}</span> : null}
          {/* Item 6: país · operadora · tipo de rede (enriquecimento offline; vazio = IP ainda não classificado pelo job). */}
          {(row.ipCountry || row.ipAsnOrg || row.ipKind) && (
            <span style={{ color: row.ipKind === 'hosting' || row.ipKind === 'tor' ? '#c62828' : '#666', marginLeft: 8, fontWeight: row.ipKind === 'hosting' || row.ipKind === 'tor' ? 600 : 400 }}>
              · {row.ipCountry || '?'}{row.ipCity || row.ipRegion ? ` (${[row.ipCity, row.ipRegion].filter(Boolean).join(', ')})` : ''}{row.ipAsnOrg ? ` · ${row.ipAsnOrg}` : ''}{row.ipKind ? ` · ${t(intl, `usage.ipt.kind.${row.ipKind}`, row.ipKind)}` : ''}
            </span>
          )}
          {(row.ipCountry || row.ipAsnOrg) && <UsageGeoAttribution intl={intl} style={{ marginTop: 2 }} />}
        </Row>
        <Row label="User-Agent" mono>{row.userAgent}</Row>
        {row.requestId && (
          <Row label={t(intl, 'usage.drawer.requestId', 'Request id (log da aplicação)')} mono>
            {row.requestId}
            {canHaveException && (
              <Button size="small" startIcon={<BugReportIcon />} style={{ marginLeft: 8 }} onClick={loadExceptions} disabled={exc.loading}
                color={row.details && row.details.error ? 'error' : 'primary'}>
                {t(intl, 'usage.drawer.viewException', 'ver exceção')}
              </Button>
            )}
          </Row>
        )}
        {reason && <Row label={t(intl, 'usage.drawer.reason', 'Motivo da resposta')}><span style={{ color: '#c62828' }}>{reason}</span></Row>}
        {isPermissionDenial(row.actionKind, row.details) && (
          <Row label={t(intl, 'usage.drawer.permission', 'IDOR (permissão): claim negada')}>
            <span style={{ color: '#6D4C41' }}>{t(intl, 'usage.kind.permission.claim', 'Claim exigida')}: <b>{row.details.claim || '-'}</b> · {t(intl, 'usage.drawer.permission.hint', 'o papel do usuário não tem essa permissão; a API respondeu 403 antes da ação')}</span>
          </Row>
        )}
        {is4xx && !reason && <Typography variant="caption" style={{ color: '#888', display: 'block', marginBottom: 10 }}>{t(intl, 'usage.drawer.no4xxException', 'Resposta 4xx tratada pela API: não gera exceção no servidor.')}</Typography>}
        {exc.loading && <Box display="flex" justifyContent="center" style={{ padding: 8 }}><CircularProgress size={20} color="secondary" /></Box>}
        {exc.loaded && exc.error && <Typography variant="body2" style={{ color: '#c62828' }}>{exc.error}</Typography>}
        {exc.loaded && !exc.error && exc.items.length === 0 && <Typography variant="body2" style={{ color: '#888' }}>{t(intl, 'usage.drawer.exception.none', 'Nenhuma exceção registrada para este request.')}</Typography>}
        {exc.items.map((e) => (
          <Box key={e.id} style={{ marginTop: 8 }}>
            <Typography variant="caption" style={{ color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t(intl, 'usage.drawer.exception.title', 'Exceção')} · {fmtDateTime(e.occurredAt, true)}{e.statusCode ? ` · HTTP ${e.statusCode}` : ''}{e.machineName ? ` · ${e.machineName}` : ''}</Typography>
            <Typography variant="body2" style={{ fontWeight: 600, wordBreak: 'break-all' }}>{e.exceptionType}</Typography>
            <Typography variant="body2" style={{ wordBreak: 'break-word' }}>{e.message}</Typography>
            {e.innerExceptionMessage && <Typography variant="body2" style={{ color: '#666', wordBreak: 'break-word' }}>↳ {e.innerExceptionMessage}</Typography>}
            {e.stackTrace && (
              <Box style={{ backgroundColor: '#1a1a2e', color: '#e0e0e0', borderRadius: 8, padding: 8, marginTop: 4, overflowX: 'auto' }}>
                <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre' }}>{e.stackTrace}</pre>
              </Box>
            )}
          </Box>
        ))}
        <Row label={t(intl, 'usage.drawer.source', 'Origem do registro')}>{row.source}</Row>

        {row.details && (
          <Box style={{ marginTop: 8 }}>
            <Typography variant="caption" style={{ color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t(intl, 'usage.drawer.details', 'Detalhes')}</Typography>
            <Box style={{ backgroundColor: '#1a1a2e', color: '#e0e0e0', borderRadius: 8, padding: 8, marginTop: 4, overflowX: 'auto' }}>
              <ReactJson src={row.details} theme="monokai" name={false} collapsed={2} displayDataTypes={false} displayObjectSize={false} enableClipboard={false} style={{ background: 'transparent', fontSize: 12 }} />
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
